import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { AvailableDeployment, DeploymentSelectionPolicy } from '@grid2/shared-types';
import { deploymentsApi } from '../../api/deployments';
import { useSession } from '../../auth/SessionContext';

interface DeploymentTargetContextValue {
  deployments: AvailableDeployment[];
  policy: DeploymentSelectionPolicy;
  selectedDeployment?: AvailableDeployment;
  loading: boolean;
  error: string | null;
  selectDeployment: (deploymentId: string) => void;
  hrefFor: (path: string) => string | undefined;
}

const fallback: DeploymentTargetContextValue = {
  deployments: [],
  policy: { selectionLocked: false },
  loading: false,
  error: null,
  selectDeployment: () => undefined,
  hrefFor: (path) => path,
};

const DeploymentTargetContext = createContext<DeploymentTargetContextValue>(fallback);

function displayName(deployment: AvailableDeployment): string {
  return deployment.config.displayName?.trim()
    || `Deployment ${deployment.deploymentId.replace(/^dep_/, '').slice(0, 8)}`;
}

export { displayName as deploymentTargetName };

export const DEPLOYMENT_TARGET_REFRESH_MS = 15_000;

export function DeploymentTargetProvider({ children }: { children: React.ReactNode }) {
  const { orgId, deploymentMode, entitlements, isLoading: sessionLoading } = useSession();
  const usesGrid2CloudOnly = deploymentMode === 'grid2-hosted'
    && Array.isArray(entitlements)
    && !entitlements.includes('self-hosting');
  const [deployments, setDeployments] = useState<AvailableDeployment[]>([]);
  const [policy, setPolicy] = useState<DeploymentSelectionPolicy>({ selectionLocked: false });
  const [selectedId, setSelectedId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (sessionLoading) {
      return;
    }

    if (usesGrid2CloudOnly) {
      setDeployments([]);
      setPolicy({ selectionLocked: false });
      setSelectedId('');
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    let refreshInFlight = false;
    const storageKey = `grid2.deployment-target.${orgId}`;

    const refresh = async (initial: boolean) => {
      if (refreshInFlight) return;
      refreshInFlight = true;
      if (initial) setLoading(true);
      try {
        const result = await deploymentsApi.available();
        if (cancelled) return;
        setDeployments(result.deployments);
        setPolicy(result.policy);
        setError(null);
        setSelectedId((current) => {
          const stored = result.policy.selectionLocked ? '' : sessionStorage.getItem(storageKey) ?? '';
          const preferred = current || stored;
          const next = result.policy.selectionLocked
            ? result.policy.defaultDeploymentId ?? result.deployments[0]?.deploymentId ?? ''
            : result.deployments.some((deployment) => deployment.deploymentId === preferred)
              ? preferred
              : result.deployments.some((deployment) => deployment.deploymentId === result.policy.defaultDeploymentId)
                ? result.policy.defaultDeploymentId ?? ''
                : result.deployments[0]?.deploymentId ?? '';
          if (next && !result.policy.selectionLocked) sessionStorage.setItem(storageKey, next);
          return next;
        });
      } catch (loadError) {
        if (cancelled) return;
        const message = loadError instanceof Error ? loadError.message : 'Deployment locations could not be loaded.';
        if (initial) {
          setDeployments([]);
          setSelectedId('');
          setError(message);
        } else {
          setError(`${message} Last known execution locations remain available.`);
        }
      } finally {
        refreshInFlight = false;
        if (!cancelled && initial) setLoading(false);
      }
    };

    setDeployments([]);
    setPolicy({ selectionLocked: false });
    setSelectedId('');
    setLoading(true);
    setError(null);
    void refresh(true);

    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') void refresh(false);
    };
    const interval = window.setInterval(refreshWhenVisible, DEPLOYMENT_TARGET_REFRESH_MS);
    window.addEventListener('focus', refreshWhenVisible);
    document.addEventListener('visibilitychange', refreshWhenVisible);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener('focus', refreshWhenVisible);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [orgId, sessionLoading, usesGrid2CloudOnly]);

  const selectDeployment = useCallback((deploymentId: string) => {
    if (policy.selectionLocked || !deployments.some((deployment) => deployment.deploymentId === deploymentId)) return;
    setSelectedId(deploymentId);
    sessionStorage.setItem(`grid2.deployment-target.${orgId}`, deploymentId);
  }, [deployments, orgId, policy.selectionLocked]);

  const selectedDeployment = deployments.find((deployment) => deployment.deploymentId === selectedId);
  const hrefFor = useCallback((path: string) => {
    if (!selectedDeployment) return path;
    if (!selectedDeployment.launchUrl) return undefined;
    try {
      return new URL(path, `${selectedDeployment.launchUrl.replace(/\/$/, '')}/`).toString();
    } catch {
      return undefined;
    }
  }, [selectedDeployment]);

  const value = useMemo<DeploymentTargetContextValue>(() => ({
    deployments, policy, selectedDeployment, loading, error, selectDeployment, hrefFor,
  }), [deployments, policy, selectedDeployment, loading, error, selectDeployment, hrefFor]);

  return <DeploymentTargetContext.Provider value={value}>{children}</DeploymentTargetContext.Provider>;
}

export function useDeploymentTarget() {
  return useContext(DeploymentTargetContext);
}
